import { Component, ViewChild, Inject, OnInit, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiService } from '../services/api.service';
import { tap, catchError, first } from 'rxjs/operators';
import { throwError, Observable, interval, Subscription, of } from 'rxjs';
import { select } from '@angular-redux/store';
import { LoggedActionService } from '../core/logged/logged.action';
import { LoggedUser } from '../core/logged/logged.model';

export interface DialogData {
  current_lang: string;
}

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss']
})
export class NavComponent implements OnInit, OnDestroy {
  @Input() set home(value: boolean) {
    if (value !== this.isHome) {
      this.isHome = value;
    }
  }
  @ViewChild('drawer') drawer: any;

  @select(['logged', 'logged'])
  user_logged$: Observable<LoggedUser>;
  user_logged;

  isLoggedIn = false;
  current_lang = 'it';

  isHome: boolean;

  superadmin = false;
  admin = false;
  editor = false;

  subscriptions: Subscription[] = [];

  isSuccessAccessOpen = false;

  constructor(private route: Router,
              private translate: TranslateService,
              public dialog: MatDialog,
              private apiservice: ApiService,
              private userAction: LoggedActionService) {
    translate.setDefaultLang('it');
    if (this.translate.currentLang) {
      this.current_lang = this.translate.currentLang;
      this.translate.use(this.current_lang);
    } else {
      this.translate.use('it');
      this.current_lang = this.translate.currentLang;
    }
    this.translate.onLangChange.subscribe(() => {
      this.current_lang = this.translate.currentLang;
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.user_logged$.subscribe( user => {
        if (!!user) {
          this.apiservice.token = user.token;
        }
        this.setLoggedUser(user);
      })
    );
    if (!!this.apiservice.token) {
      this.apiservice.refreshToken().pipe(
        catchError((err) => {
          this.userAction.setLoggedUserCurrent(null);
          return of(err);
        })
      ).subscribe();
    }
    // Jwt expiration almost a day
    const dayMs = (23 * 60 + 30) * 60 * 1000;
    // const dayMs = 15 * 1000;
    const source = interval(dayMs);
    this.subscriptions.push(
      source.subscribe(() => {
        if (!!this.apiservice.token) {
          this.apiservice.refreshToken().pipe(
            catchError((err) => {
              this.userAction.setLoggedUserCurrent(null);
              return of(err);
            })
          ).subscribe();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  setLoggedUser(value_from_subscribe: LoggedUser, from_login = false) {
    this.user_logged = value_from_subscribe;
    this.superadmin = false;
    this.admin = false;
    this.editor = false;
    this.isLoggedIn = false;
    if (value_from_subscribe !== null) {
      this.isLoggedIn = true;
      if (this.user_logged.isadmin) {
        this.superadmin = true;
      } else if (this.user_logged.auth_user === 'admin') {
        this.admin = true;
      } else if (this.user_logged.auth_user === 'editor') {
        this.editor = true;
      }
    } else {
      this.apiservice.token = undefined;
    }
  }

  navFunction(path_to_new_page) {
    if (this.drawer.opened == true) {
      this.drawer.opened = false;
    }
    this.route.navigate([path_to_new_page]);
  }

  changeLanguage(chosen_language: string) {
    if (this.drawer.opened === true) {
      this.drawer.opened = false;
    }
    this.translate.use(chosen_language);
  }

  logInOrLogOut() {
    if (this.drawer.opened === true) {
      this.drawer.opened = false;
    }

    if (this.isLoggedIn) {
      const dialogRef = this.dialog.open(LogoutDialogContent, {
        width: '450px'
      });
    } else {
      const dialogRef = this.dialog.open(LoginDialogContent, {
        width: '450px',
        data: {current_lang: this.current_lang}
      });

      dialogRef.afterClosed().pipe(first()).subscribe((result) => {
        if (result === 'success') {
          this.dialog.open(SuccessLoginComponent, {
            width: '450px'
          });
        }
      });
    }
  }

  redirectToPrimis() {
    window.location.href = 'https://2014-2020.ita-slo.eu/it/primis'; 
  }

  redirectToPrimisPlus() {
    window.location.href = 'https://www.ita-slo.eu/it/primis-plus';
  }
}

@Component({
  selector: 'login-dialog-content',
  templateUrl: 'login-dialog-content.html',
  styleUrls: ['./nav.component.scss']
})
export class LoginDialogContent {
  credentials = {
    username: '',
    password: ''
  };
  error_cred = false;

  showForgotPwdInfo = false;

  constructor(
    public dialogRef: MatDialogRef<LoginDialogContent>,
    private userAction: LoggedActionService,
    private apiservice: ApiService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData) {}

  loginRequest(): void {
    this.apiservice.loginUser(this.credentials).pipe(
      tap((x: any) => {
        if (x && !x.error) {
          this.userAction.setLoggedUserCurrent(LoggedUser.from(x));
          this.dialogRef.close('success');
        }
      }),
      catchError(error => {
        this.error_cred = true;
        return throwError(error);
      })
    ).subscribe();
  }
}

@Component({
  selector: 'logout-dialog-content',
  templateUrl: 'logout-dialog-content.html',
  styleUrls: ['./nav.component.scss']
})
export class LogoutDialogContent {

  constructor(
    public dialogRef: MatDialogRef<LogoutDialogContent>,
    private userAction: LoggedActionService,
    private route: Router) {}

  logoutRequest(): void {
    this.userAction.setLoggedUserCurrent(null);
    this.dialogRef.close();
    this.route.navigate(['/home']);
  }
}
@Component({
  selector: 'success-login',
  templateUrl: 'success-login.component.html',
  styleUrls: ['./nav.component.scss']
})
export class SuccessLoginComponent {

  constructor(public dialogRef: MatDialogRef<SuccessLoginComponent>) {}
}
